/**
 * Media identity helpers shared by the Whiteboards and Databases frontends.
 *
 * Content migrations (BRIE / C2C) can persist marker strings in place of a real Media
 * API file ID when a file could not be exported or could not be associated with the
 * destination collection. These markers are not valid Media API file IDs — every media
 * identity path must recognise them and skip the Media API call rather than issuing
 * doomed requests.
 *
 * The two marker kinds have different long-term fates:
 * - `omitted-mediaId-*` — the file was permanently omitted from the migration export.
 * - `{mediaId}-{migrationId}-unassociated` — the file exists but was not yet linked to
 *   the destination collection and can be repaired using the embedded real UUID.
 *
 * Use `isMigrationPlaceholderMediaId` as a "do not call Media with this id" predicate.
 * Do not treat it as a "render permanently missing" predicate — only omitted media is
 * permanently missing.
 *
 * Keep in sync with `services/canvas-server/.../services/media-id-utils.ts`.
 */

/**
 * Prefix of the marker written when a file was omitted from the migration export, i.e.
 * `omitted-mediaId` or `omitted-mediaId-{migrationId}`.
 */
export const OMITTED_MEDIA_ID_PREFIX = 'omitted-mediaId';

/**
 * Suffix of the marker written when a file was exported but could not be associated with
 * the destination collection, i.e. `{mediaId}-{migrationId}-unassociated`.
 */
export const UNASSOCIATED_MEDIA_ID_SUFFIX = '-unassociated';

/**
 * Matches the legacy `confluence:` prefix that older documents stored on media IDs.
 */
export const LEGACY_MEDIA_ID_PREFIX_REGEX = /^confluence:/;

const UUID_PATTERN =
  '[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';
const UUID_REGEX = new RegExp(`^${UUID_PATTERN}$`, 'i');
const UNASSOCIATED_MEDIA_ID_REGEX = new RegExp(
  `^(${UUID_PATTERN})-.+${UNASSOCIATED_MEDIA_ID_SUFFIX}$`,
  'i',
);

/**
 * Removes the legacy `confluence:` prefix from a media ID.
 *
 * @example
 *   sanitizeMediaId('confluence:abc-123'); // 'abc-123'
 */
export const sanitizeMediaId = (mediaId: string): string =>
  mediaId.replace(LEGACY_MEDIA_ID_PREFIX_REGEX, '');

/**
 * Whether a media ID is a UUID, i.e. addressable by the Media API.
 */
export const isValidMediaId = (mediaId: string): boolean => UUID_REGEX.test(mediaId);

/**
 * Whether the media ID marks a file that was omitted from a migration export.
 */
export const isOmittedMediaIdPlaceholder = (
  mediaId: string | null | undefined,
): boolean => {
  if (typeof mediaId !== 'string') {
    return false;
  }

  const sanitized = sanitizeMediaId(mediaId);

  return (
    sanitized === OMITTED_MEDIA_ID_PREFIX ||
    sanitized.startsWith(`${OMITTED_MEDIA_ID_PREFIX}-`)
  );
};

/**
 * Whether the media ID marks a file that a migration could not associate with the
 * destination media collection.
 */
export const isUnassociatedMediaIdPlaceholder = (
  mediaId: string | null | undefined,
): boolean =>
  typeof mediaId === 'string' &&
  sanitizeMediaId(mediaId).endsWith(UNASSOCIATED_MEDIA_ID_SUFFIX);

/**
 * Returns the real Media API UUID embedded in a well-formed unassociated marker.
 */
export const extractMediaIdFromUnassociatedPlaceholder = (
  mediaId: string | null | undefined,
): string | undefined => {
  if (typeof mediaId !== 'string') {
    return undefined;
  }

  return UNASSOCIATED_MEDIA_ID_REGEX.exec(sanitizeMediaId(mediaId))?.[1];
};

/**
 * Whether the media ID is a migration marker rather than a real Media API file ID.
 *
 * Returns `true` for both `omitted-mediaId-*` and `{mediaId}-{migrationId}-unassociated`
 * markers. In both cases the marker string must not be passed to the Media API. Omitted
 * media is permanently missing; unassociated media can be repaired using its embedded UUID.
 *
 * Use this as a "do not call Media with this id" predicate, not as a
 * "render permanently missing" predicate.
 */
export const isMigrationPlaceholderMediaId = (
  mediaId: string | null | undefined,
): boolean =>
  isOmittedMediaIdPlaceholder(mediaId) || isUnassociatedMediaIdPlaceholder(mediaId);
